// Where the HUD covers the map, so map labels stay out from under it. Reads the interactive widgets
// (pointer-events on) of the layers that lie over the map container, sampled a few times a second:
// the HUD moves rarely, and a style read per element per frame would cost more than it saves.
//
//   #app ─┬─ #map  ── labels.root      (the map container: its box is the label coordinate space)
//         └─ #hud  ── panels, ribbon, minimap …   (pointer-events: auto ones are the obstacles)

/** A screen box [x0, y0, x1, y1] in label-layer pixels. */
export type ScreenRect = readonly [number, number, number, number];

const REFRESH_MS = 400;
const PAD_PX = 6;
// A widget covering most of the screen is a backdrop or a modal: labels under it are simply hidden.
const BACKDROP_SHARE = 0.6;

function rectOf(el: Element, origin: DOMRect, area: number): ScreenRect | null {
  const box = el.getBoundingClientRect();
  const size = box.width * box.height;
  if (size <= 0 || size > area * BACKDROP_SHARE) {
    return null;
  }

  return [box.left - origin.left - PAD_PX, box.top - origin.top - PAD_PX, box.right - origin.left + PAD_PX, box.bottom - origin.top + PAD_PX];
}

/** Interactive boxes below `layer`, without descending into one (its children lie inside it). */
function collect(layer: Element, origin: DOMRect, area: number, out: ScreenRect[]): void {
  for (const child of Array.from(layer.children)) {
    const style = getComputedStyle(child);
    if (style.display === 'none' || style.visibility === 'hidden') {
      continue;
    }

    if (style.pointerEvents === 'none') {
      collect(child, origin, area, out);
      continue;
    }

    const rect = rectOf(child, origin, area);
    if (rect) {
      out.push(rect);
    }
  }
}

/** A reader of the HUD's boxes over the map container that holds `root`; empty when there is none. */
export function hudRects(root: HTMLElement): () => readonly ScreenRect[] {
  let at = -Infinity;
  let rects: ScreenRect[] = [];

  return () => {
    const now = performance.now();
    if (now - at < REFRESH_MS) {
      return rects;
    }

    at = now;
    rects = [];
    const map = root.parentElement;
    const app = map?.parentElement;
    if (!map || !app || typeof getComputedStyle !== 'function') {
      return rects;
    }

    const origin = root.getBoundingClientRect();
    const area = origin.width * origin.height;
    for (const layer of Array.from(app.children)) {
      if (layer !== map) {
        collect(layer, origin, area, rects);
      }
    }
    return rects;
  };
}
